import { addDays, daysBetween } from './iso-week'
import { WEEKDAY_NAMES } from './meeting-due'

/**
 * Rotinas (SIS-436): atividade recorrente de uma pessoa ou do time da área.
 *
 * As ocorrências não são gravadas: saem da regra para o período pedido. Só a
 * marcação (feita / não feita) mora no banco (`supply.routine_check`). O banco
 * tem a mesma regra em `supply.routine_occurs_on` — os testes (`routine.test.ts`
 * e `supabase/tests/020`) usam os mesmos casos.
 *
 * Fora da régua de desempenho: ocorrência não é tarefa do quadro.
 */

export const ROUTINE_FREQS = ['diaria', 'semanal', 'mensal'] as const
export type RoutineFreq = (typeof ROUTINE_FREQS)[number]

export const ROUTINE_FREQ_LABEL: Record<RoutineFreq, string> = {
  diaria: 'Diária (seg–sex)',
  semanal: 'Semanal',
  mensal: 'Mensal',
}

/** Rótulo curto dos dias (0 = domingo, como `meeting_schedule.dia_semana`). */
export const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const

export interface RoutineRule {
  readonly freq: RoutineFreq
  /** 0 = domingo … 6 = sábado (semanal). */
  readonly weekdays: readonly number[]
  /** 1–31 (mensal); mês sem o dia usa o último dia. */
  readonly month_day: number | null
  readonly starts_on: string
  readonly ends_on: string | null
}

function parts(iso: string): [number, number, number] {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  return [y, m, d]
}

function weekdayOf(iso: string): number {
  const [y, m, d] = parts(iso)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

function lastDayOfMonth(iso: string): number {
  const [y, m] = parts(iso)
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

/** A rotina acontece no dia? (espelho de `supply.routine_occurs_on`) */
export function routineOccursOn(rule: RoutineRule, day: string): boolean {
  if (day < rule.starts_on) return false
  if (rule.ends_on && day > rule.ends_on) return false
  const dow = weekdayOf(day)
  if (rule.freq === 'diaria') return dow >= 1 && dow <= 5
  if (rule.freq === 'semanal') return rule.weekdays.includes(dow)
  if (rule.month_day === null) return false
  return parts(day)[2] === Math.min(rule.month_day, lastDayOfMonth(day))
}

/** Datas da rotina entre `from` e `to` (inclusive), em ordem. */
export function routineOccurrences(rule: RoutineRule, from: string, to: string): string[] {
  const out: string[] = []
  const start = from < rule.starts_on ? rule.starts_on : from
  const end = rule.ends_on && rule.ends_on < to ? rule.ends_on : to
  const n = daysBetween(start, end)
  for (let i = 0; i <= n; i++) {
    const day = addDays(start, i)
    if (routineOccursOn(rule, day)) out.push(day)
  }
  return out
}

/** "Seg, Sex", "Todo dia 31 (ou o último)", "Seg a sex". */
export function routineRuleLabel(rule: Pick<RoutineRule, 'freq' | 'weekdays' | 'month_day'>): string {
  if (rule.freq === 'diaria') return 'Seg a sex'
  if (rule.freq === 'semanal') {
    const days = [...rule.weekdays].sort((a, b) => a - b)
    if (days.length === 7) return 'Todos os dias'
    return days.map((d) => WEEKDAY_SHORT[d]).join(', ')
  }
  const d = rule.month_day ?? 1
  return d >= 29 ? `Todo dia ${d} (ou o último)` : `Todo dia ${d}`
}

const joinPt = (items: readonly string[]): string =>
  items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`

/**
 * A regra numa frase que se lê em voz alta: "Toda segunda e quinta",
 * "Todo sábado", "Todo dia útil (seg a sex)", "Todo dia 5". Sábado e domingo
 * são masculinos ("todo"); os dias úteis, femininos ("toda").
 */
export function routineRuleSentence(rule: Pick<RoutineRule, 'freq' | 'weekdays' | 'month_day'>): string {
  if (rule.freq === 'diaria') return 'Todo dia útil (seg a sex)'
  if (rule.freq === 'mensal') {
    const d = rule.month_day
    if (d === null || !Number.isInteger(d) || d < 1 || d > 31) return 'Escolha o dia do mês'
    return d >= 29 ? `Todo dia ${d} (ou o último dia do mês)` : `Todo dia ${d}`
  }
  const days = [...new Set(rule.weekdays)].filter((d) => d >= 0 && d <= 6).sort((a, b) => a - b)
  if (days.length === 0) return 'Escolha os dias da semana'
  if (days.length === 7) return 'Todos os dias'
  if (days.join() === '1,2,3,4,5') return 'Todo dia útil (seg a sex)'
  if (days.join() === '0,6') return 'Todo fim de semana'
  const weekdays = days.filter((d) => d >= 1 && d <= 5).map((d) => WEEKDAY_NAMES[d] ?? '')
  // Domingo vem depois do sábado na frase ("todo sábado e domingo").
  const weekend = [6, 0].filter((d) => days.includes(d)).map((d) => WEEKDAY_NAMES[d] ?? '')
  const parts: string[] = []
  if (weekdays.length) parts.push(`toda ${joinPt(weekdays)}`)
  if (weekend.length) parts.push(`todo ${joinPt(weekend)}`)
  const text = parts.join(weekdays.length > 1 ? ', e ' : ' e ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Próxima data da rotina a partir de `from` (inclusive), olhando até ~2 meses; `null` se não houver. */
export function nextRoutineOccurrence(rule: RoutineRule, from: string): string | null {
  return routineOccurrences(rule, from, addDays(from, 62))[0] ?? null
}

/** Erro de validação da regra (o banco recusa o mesmo), ou `null`. */
export function routineRuleError(rule: RoutineRule): string | null {
  if (rule.freq === 'semanal' && rule.weekdays.length === 0) return 'Marque pelo menos um dia da semana'
  if (rule.freq === 'semanal' && rule.weekdays.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) return 'Dia da semana inválido'
  if (rule.freq === 'mensal' && (rule.month_day === null || !Number.isInteger(rule.month_day) || rule.month_day < 1 || rule.month_day > 31))
    return 'Dia do mês entre 1 e 31'
  if (rule.ends_on && rule.ends_on < rule.starts_on) return 'O fim não pode ser antes do início'
  return null
}

// ---------------------------------------------------------------------------
// Ocorrência e aderência
// ---------------------------------------------------------------------------

/**
 * Estado da ocorrência:
 * - `feita` / `nao_feita`: marcada;
 * - `pendente`: hoje ou no futuro, sem marcação;
 * - `perdida`: passou o dia sem marcação (conta como não feita na aderência).
 */
export type OccurrenceState = 'feita' | 'nao_feita' | 'pendente' | 'perdida'

export const OCCURRENCE_STATE_LABEL: Record<OccurrenceState, string> = {
  feita: 'Feita',
  nao_feita: 'Não feita',
  pendente: 'Pendente',
  perdida: 'Sem marcação',
}

export function occurrenceState(day: string, done: boolean | null | undefined, today: string): OccurrenceState {
  if (done === true) return 'feita'
  if (done === false) return 'nao_feita'
  return day < today ? 'perdida' : 'pendente'
}

export interface Adherence {
  readonly feitas: number
  /** Não feitas + sem marcação em dia que já passou. */
  readonly naoFeitas: number
  /** Hoje/futuro sem marcação: ainda não contam. */
  readonly pendentes: number
  /** 0–100, ou `null` sem ocorrência vencida. */
  readonly pct: number | null
}

/**
 * Aderência = feitas ÷ (feitas + não feitas + sem marcação em dia passado).
 * Ocorrência de hoje sem marcação ainda não conta (o dia não acabou).
 */
export function routineAdherence(states: readonly OccurrenceState[]): Adherence {
  let feitas = 0
  let naoFeitas = 0
  let pendentes = 0
  for (const s of states) {
    if (s === 'feita') feitas++
    else if (s === 'pendente') pendentes++
    else naoFeitas++
  }
  const base = feitas + naoFeitas
  return { feitas, naoFeitas, pendentes, pct: base ? Math.round((feitas / base) * 100) : null }
}
